import { LockOutlined, UsergroupAddOutlined, UserOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Collapse, Form, Input, Select } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { imgHandler } from "../../ImgHandler";
import background from "../../assets/images/background.jpg";
import { changeShowLoading } from "../../components/Spinner/Spinner";
import { isAuthenticatedState } from "../../recoil/atoms/authenticationAtom";
import { userInfoState } from "../../recoil/atoms/userInfoAtom";
import AuthService from "../../services/auth.service";
import CookieService from "../../services/cookie.service";
import VersionService, { BackendVersion, FrontendVersion } from "../../services/version.service";
import sixConfig from "../../sixConfig";
import { genericResponse as GenericResponse } from "../../types/axios.types";
import { SurgeryRoomType } from "../../types/general.types";
import {
  LoginForm,
  SecretCounter,
  User,
  UserInfoResponse,
  UserRoleResponse,
} from "../../types/login.types";
import "./Login.css";

const { Panel } = Collapse;


function Login() {
  let navigate = useNavigate();

  // eslint-disable-next-line
  const [_, setIsAuthenticated] = useRecoilState(isAuthenticatedState);
  // eslint-disable-next-line
  const [__, setUserInfo] = useRecoilState(userInfoState);
  // eslint-disable-next-line
  const [selectedUser, setSelectedUser] = useState({} as User);
  const [secretCounter, setSecretCounter] = useState({} as SecretCounter);
  const [userRoles, setUserRoles] = useState({} as UserRoleResponse);
  const [backendVersion, setBackendVersion] = useState<BackendVersion | null>(null);
  const [frontendVersion, setFrontendVersion] = useState<FrontendVersion | null>(null);
  const [showVersionDetail, setShowVersionDetail] = useState(false);
  const { t, i18n } = useTranslation();
  const [hideUrlBe, setHideUrlBe] = useState(false);
  const [isConfigurationModalOpen, setIsConfigurationModalOpen] =
    useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [surgeryRoomList, setSurgeryRoomList] = useState(
    [] as SurgeryRoomType[]
  );

  const [form] = Form.useForm();

  const customer = sixConfig.APP_CUSTOMER;
  const onFinish = async (values: LoginForm) => {
    const login: GenericResponse = await AuthService.loginRequest(values);
    if (login.status === 200) {
      setIsAuthenticated(true);
      const userInfo: UserInfoResponse =
        await AuthService.retrieveLoggedUserInfo();
      setUserInfo(userInfo);
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      navigate("/surgeries");
    }
  };

  const loadUserRole = async () => {

    changeShowLoading(AuthService.retrieveUserInRole()
      .then((response: UserRoleResponse) => {
        setUserRoles(response);
      })
      .catch((e) => {
        console.log(e);
      }));
  };

  const loadVersions = async () => {
    try {
      const [beVersion, feVersion] = await Promise.all([
        VersionService.getBackendVersion(),
        VersionService.getFrontendVersion().catch(() => null)
      ]);
      setBackendVersion(beVersion);
      setFrontendVersion(feVersion);
    } catch (e) {
      console.log('Error loading versions:', e);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const setLongDescription = (key: string) => {
    let longDescription = key.toUpperCase() + "_LONG_DESCRIPTION";
    return t(longDescription);
  };

  const onSelectedUserChanged = (user: User) => {
    form.setFieldsValue({ username: user.username });
    setSelectedUser(user);
  };

  const onLogoClick = () => {
    const currentCounter = secretCounter?.counter || 1;
    if (currentCounter === 3) {
      setSecretCounter({ counter: 1 });
      navigate("/settings");
    } else {
      setSecretCounter({ counter: currentCounter + 1 });
    }
  };

  /**
   * MODAL start
   */



  const handleOk = () => {
    setIsConfigurationModalOpen(false);
  };

  const handleCancel = () => {
    setIsConfigurationModalOpen(false);
  };


  const loadSurgeryRoomList = () => {
    changeShowLoading(AuthService.retrieveSurgeryRoomList().then((resp) => {
      setSurgeryRoomList(resp.list);
    }));
  };


  const initMonitorParams = async () => {
    const queryParams = new URLSearchParams(window.location.search)
    const urlBe = queryParams.get("url_be")
    if (urlBe) {
      setHideUrlBe(true);
      CookieService.setApiUrl(urlBe);
    }
  };

  /**
   * MODAL end
   */

  useEffect(() => {
    initMonitorParams();
    const apiUrl = CookieService.getApiUrl();
    if (!apiUrl || apiUrl === "") {
      return;
    }
    loadUserRole();
    loadVersions();
    
    // Clear localStorage on first render
    localStorage.removeItem('userInfo');
    localStorage.removeItem('patientData');
    localStorage.removeItem('current.status');
    localStorage.removeItem('current.surgery');
    localStorage.removeItem('current.checklists');
  }, []);

  return (
    <>
      <div className="grid grid-cols-12 h-screen">
        <div className="background-image col-span-8 bg-gradient-to-r from-sky-500 to-indigo-500 relative">
          <img src={background} className="w-full h-full bg-no-repeat" alt="" />
          <div className="w-1/2 h-1/2 absolute top-1/3 left-1/4 rounded-lg p-4">
            <div className="col-span-4 mt-2 bg-white p-4 rounded-lg shadow-lg" style={{
              background: "rgba(255, 255, 255, 0.5)"
            }}>
              <Select
                placeholder={t("MEDIC")}
                suffixIcon={
                  <div className="text-teal-700 font-bold">
                    {t("SEARCH_ROLE")}{" "}
                    <UsergroupAddOutlined></UsergroupAddOutlined>
                  </div>
                }
                style={{ width: "100%", height: "3rem" }}
                onChange={(value) => {
                  setSelectedRole(value);
                }}
              >
                {Object.keys(userRoles)
                  .sort()
                  .map((key: string) => (
                    <Select.Option key={key} value={key}>
                      {setLongDescription(key)}
                    </Select.Option>
                  ))}
              </Select>
              {selectedRole && (
                <div className="mt-1">
                  <Select
                    placeholder={"Mario Rossi"}
                    suffixIcon={
                      <div className="text-teal-700 font-bold">
                        {t("SEARCH_OPERATOR")} <UserOutlined></UserOutlined>
                      </div>
                    }
                    style={{ width: "100%", marginTop: "1rem", height: "3rem" }}
                    onChange={(uuid: string) => {
                      onSelectedUserChanged(userRoles[selectedRole].find(
                        (user) => user.operatoruuid === uuid
                      ) as User);
                    }}
                  >
                    {userRoles[selectedRole].map((user, index: number) => (
                      <Select.Option
                        key={index}
                        value={user.operatoruuid}
                      >
                        {user.operatorDescription || user.username}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-span-4 bg-white">
          <div className="login-form">
            <Form
              name="normal_login"
              className="login-form mx-28 my-80 w-80 h-80"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              form={form}
            >
              <Form.Item>
                <div
                  className="logo-container flex justify-center"
                  onClick={onLogoClick}
                >
                  <img src={require(`../../assets/images/${imgHandler?.[customer]?.src_login_top}.jpg`)} className={`${imgHandler?.[customer]?.style_top}`} alt="" />
                </div>
              </Form.Item>
              <Form.Item
                className="w-72 ml-4"
                name="username"
                rules={[
                  {
                    required: true,
                    message: `${t('ENTER_USERNAME')}`,
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined className="site-form-item-icon" />}
                  placeholder={`${t('USERNAME')}`}
                />
              </Form.Item>
              <Form.Item
                className="w-72 ml-4"
                name="password"
                rules={[
                  {
                    required: true,
                    message: `${t('ENTER_PASSWORD')}`,
                  },
                ]}
              >
                <Input
                  prefix={<LockOutlined className="site-form-item-icon" />}
                  type="password"
                  placeholder={`${t('PASSWORD')}`}
                />
              </Form.Item>
              <Form.Item>
                <div className="flex justify-center">
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="login-form-button bg-cyan-500 w-28 font-bold"
                  >
                    {t('LOG_IN')}
                  </Button>
                </div>
              </Form.Item>
              <Form.Item>
                <div className="logo-container flex justify-center">
                  {imgHandler[customer].src_login_btm ? <>
                    <img src={require(`../../assets/images/${imgHandler?.[customer]?.src_login_btm}.jpg`)} className={`${imgHandler?.[customer]?.style_btn}`} alt="" />
                  </> : ''}
                </div>
              </Form.Item>
              <div id="version" className="flex justify-center text-lg">
                <div className="flex-col text-center">
                  {/* Se BE version disponibile: nuova visualizzazione con dettaglio espandibile */}
                  {backendVersion?.version ? (
                    <>
                      <div 
                        className="cursor-pointer flex items-center justify-center gap-1"
                        onClick={() => setShowVersionDetail(!showVersionDetail)}
                      >
                        <span>{sixConfig?.APP_NAME} v.{backendVersion.version}</span>
                        <InfoCircleOutlined className="text-gray-500 text-sm" />
                      </div>
                      {showVersionDetail && (
                        <div className="mt-2 text-sm text-gray-600 border-t pt-2">
                          <div className="font-semibold mb-1">{t('VERSION_DETAILS') || 'Dettaglio Versioni'}</div>
                          <div className="grid grid-cols-1 gap-1 text-left">
                            <div className="mb-1">
                              <span className="font-medium">Backend:</span> {backendVersion?.version || '-'}
                              <div className="text-xs ml-2">
                                <span>Commit: {backendVersion?.commit_hash?.substring(0, 7) || '-'}</span>
                                <span className="ml-2">Branch: {backendVersion?.branch || '-'}</span>
                                <span className="ml-2">Data: {backendVersion?.release_date ? new Date(backendVersion.release_date).toLocaleDateString('it-IT') : '-'}</span>
                              </div>
                            </div>
                            <div className="mb-1">
                              <span className="font-medium">Frontend (UI):</span> {frontendVersion?.commit?.substring(0, 7) || '-'}
                              <div className="text-xs ml-2">
                                <span>Commit: {frontendVersion?.commit?.substring(0, 7) || '-'}</span>
                                <span className="ml-2">Branch: {frontendVersion?.branch || '-'}</span>
                                <span className="ml-2">Data: {frontendVersion?.timestamp ? new Date(frontendVersion.timestamp).toLocaleDateString('it-IT') : '-'}</span>
                              </div>
                            </div>
                            <div className="mb-1">
                              <span className="font-medium">Monitor:</span> {sixConfig?.VERSION}
                              <div className="text-xs ml-2">
                                <span>Commit: {sixConfig?.REV || '-'}</span>
                                <span className="ml-2">Branch: {sixConfig?.BRANCH || '-'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    /* Fallback: visualizzazione vecchia solo Monitor */
                    <>
                      <div>{sixConfig?.APP_NAME}  v.{sixConfig?.VERSION}</div>
                      <div className="text-sm text-gray-600">{sixConfig?.REV} ({sixConfig?.BRANCH})</div>
                    </>
                  )}
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
